// src/pages/Viewer.tsx
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  addDoc,
  collection,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  where,
} from "firebase/firestore";
import { ReactElementFactory, Survey } from "survey-react-ui";
import "survey-core/survey-core.css";
import "./viewer-survey-overrides.css";
import { CustomError, Model, Question, QuestionPanelDynamicModel, Serializer } from "survey-core";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";

import { auth, db } from "../firebase";
import { signInAnonymously } from "firebase/auth";
import { useSurveyAccess } from "../hooks/useSurveyAccess";
import { rankChoiceMatches } from "../utils/choiceSearch";

let isFirestoreListPropertyRegistered = false;
let isPanelDynamicOtherUniquenessPatched = false;
let isPercentageProgressRegistered = false;

const OTHER_KEY_PREFIX = "__other__:";
type AddPromptKind = "organization" | "person";

class LocalKeyDuplicationError extends CustomError {
  constructor(text: string, errorOwner?: any) {
    super(text, errorOwner);
  }

  public getErrorType(): string {
    return "keyduplicationerror";
  }
}

function normalizeOtherValueForKey(question: Question): string | null {
  const baseValue = question.value;
  if (baseValue !== "other") {
    return null;
  }

  const rawComment = (question as any).comment;
  if (typeof rawComment !== "string") {
    return null;
  }

  const trimmed = rawComment.trim();
  if (!trimmed) {
    return null;
  }

  return `${OTHER_KEY_PREFIX}${trimmed.toLocaleLowerCase()}`;
}

function ensurePanelDynamicOtherUniquenessPatch() {
  if (isPanelDynamicOtherUniquenessPatched) {
    return;
  }

  const proto = QuestionPanelDynamicModel.prototype as any;
  const original = proto.isValueDuplicated;

  proto.isValueDuplicated = function (panel: any, keyValues: any[], rec?: any, fireCallback?: boolean) {
    const question =
      panel && typeof panel.getQuestionByValueName === "function" && this.keyName
        ? panel.getQuestionByValueName(this.keyName)
        : undefined;

    if (!question || question.isEmpty()) {
      return original.call(this, panel, keyValues, rec, fireCallback);
    }

    const normalizedOtherValue = normalizeOtherValueForKey(question);
    if (!normalizedOtherValue) {
      return original.call(this, panel, keyValues, rec, fireCallback);
    }

    if (!!this.changingValueQuestion && question !== this.changingValueQuestion) {
      question.hasErrors(fireCallback, rec);
    }

    for (let i = 0; i < keyValues.length; i++) {
      if (normalizedOtherValue == keyValues[i]) {
        if (fireCallback) {
          question.addError(new LocalKeyDuplicationError(this.keyDuplicationError, this));
        }
        if (!!rec && !rec.firstErrorQuestion) {
          rec.firstErrorQuestion = question;
        }
        return true;
      }
    }
    keyValues.push(normalizedOtherValue);
    return false;
  };

  isPanelDynamicOtherUniquenessPatched = true;
}

ensurePanelDynamicOtherUniquenessPatch();

function SurveyPercentageProgress(props: { survey?: Model; model?: { title?: string } }) {
  const survey = props.survey;
  const [progressValue, setProgressValue] = useState(() => Math.round(survey?.progressValue ?? 0));

  useEffect(() => {
    if (!survey) return;

    const updateProgress = () => setProgressValue(Math.round(survey.progressValue ?? 0));
    updateProgress();

    const callback = () => updateProgress();
    (survey as any).addOnPropertyValueChangedCallback?.(callback);
    (survey as any).onCurrentPageChanged?.add?.(callback);
    (survey as any).onValueChanged?.add?.(callback);

    return () => {
      (survey as any).removeOnPropertyValueChangedCallback?.(callback);
      (survey as any).onCurrentPageChanged?.remove?.(callback);
      (survey as any).onValueChanged?.remove?.(callback);
    };
  }, [survey]);

  if (!survey) return null;

  const title = props.model?.title || "Survey progress";
  const boundedProgress = Math.max(0, Math.min(100, progressValue));

  return (
    <div className="stakeholder-progress" aria-label={title}>
      <div className="stakeholder-progress__header">
        <span>{title}</span>
        <span>{boundedProgress}%</span>
      </div>
      <div className="stakeholder-progress__track">
        <div
          className="stakeholder-progress__bar"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={boundedProgress}
          style={{ width: `${boundedProgress}%` }}
        />
      </div>
    </div>
  );
}

function ensurePercentageProgressRegistered() {
  if (isPercentageProgressRegistered) return;

  ReactElementFactory.Instance.registerElement("stakeholder-progress-percentage", (props) => (
    <SurveyPercentageProgress survey={props.survey as Model} model={props.model} />
  ));
  isPercentageProgressRegistered = true;
}

export default function Viewer() {
  const { id } = useParams<{ id: string }>();
  const [surveyModel, setSurveyModel] = useState<Model | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [orgConfirmOpen, setOrgConfirmOpen] = useState(false);
  const [addPromptKind, setAddPromptKind] = useState<AddPromptKind>("organization");

  const unsubscribersRef = useRef<Array<() => void>>([]);
  const submittingRef = useRef(false);
  const orgConfirmResolverRef = useRef<((proceed: boolean) => void) | null>(null);
  const lastOrganizationPromptAcceptedAtRef = useRef(0);

  const requestOrganizationConfirm = (kind: AddPromptKind) => {
    return new Promise<boolean>((resolve) => {
      orgConfirmResolverRef.current = resolve;
      setAddPromptKind(kind);
      setOrgConfirmOpen(true);
    });
  };

  const resolveOrganizationConfirm = (proceed: boolean) => {
    const resolver = orgConfirmResolverRef.current;
    orgConfirmResolverRef.current = null;
    setOrgConfirmOpen(false);
    if (resolver) resolver(proceed);
  };

  useEffect(() => {
    return () => {
      if (orgConfirmResolverRef.current) {
        orgConfirmResolverRef.current(false);
        orgConfirmResolverRef.current = null;
      }
    };
  }, []);

  // Centralized access (the hook should allow public viewing if survey is published)
  const { loading, allowed, role, survey } = useSurveyAccess(id!, "view");

  // Silent anonymous sign-in so list "Other" adds are allowed by rules
  useEffect(() => {
    if (!auth.currentUser) {
      signInAnonymously(auth).catch(() => {
        // non-fatal: viewing still works; list item adds may fail without auth
      });
    }
  }, []);

  // Build SurveyJS model once we have access + survey
  useEffect(() => {
    // cleanup any previous subscriptions
    unsubscribersRef.current.forEach((u) => {
      try {
        u();
      } catch {}
    });
    unsubscribersRef.current = [];

    if (loading) return;
    if (!allowed || !survey?.json) {
      setSurveyModel(null);
      if (!loading && !allowed) setError("Survey not available.");
      return;
    }

    // Register custom property once
    if (!isFirestoreListPropertyRegistered) {
      Serializer.addProperty("dropdown", { name: "firestoreListId", type: "string" });
      isFirestoreListPropertyRegistered = true;
    }

    const model = new Model(survey.json);
    model.showCompletedPage = true;

    model.onChoicesSearch.add((_, options: any) => {
      const listId = options?.question?.getPropertyValue?.("firestoreListId");
      if (!listId) return;

      const choices = options.choices ?? [];
      const rankedChoices = rankChoiceMatches(
        choices,
        options.filter,
        (choice: any) => choice?.text ?? choice?.value
      );

      options.filteredChoices = options.filter ? rankedChoices : undefined;

      // SurveyJS uses filteredChoices as a membership filter but retains the
      // dropdown's original order, so update the rendered list with our ranking.
      options.question?.dropdownListModel?.listModel?.setItems(
        options.filter ? rankedChoices : choices
      );
    });

    if (survey.viewerSettings?.progressBar === "percentage") {
      ensurePercentageProgressRegistered();
      model.showProgressBar = false;
      model.addLayoutElement({
        id: "stakeholder-progress-percentage",
        container: "contentTop",
        component: "stakeholder-progress-percentage",
        data: { title: "Survey progress" },
        index: 0,
      });
    }

    const organizationLabelRegex = /\borgani[sz]ations?\b/;
    const personLabelRegex = /\b(?:person|people)\b/;

    const promptKindForText = (text: string): AddPromptKind | null => {
      if (organizationLabelRegex.test(text)) return "organization";
      if (personLabelRegex.test(text)) return "person";
      return null;
    };

    const getAddPromptKindOnCurrentPage = (options: any): AddPromptKind | null => {
      const oldPage = options?.oldCurrentPage ?? model.currentPage;
      const pageQuestions: any[] = oldPage?.questions ?? [];

      for (const q of pageQuestions) {
        if (q?.getType?.() !== "paneldynamic") continue;

        const addPanelText = String(q.addPanelText ?? "").toLocaleLowerCase();
        const promptKind = promptKindForText(addPanelText);
        const canAdd = q.canAddPanel !== false;
        const isVisible = q.isVisible !== false;
        const isReadOnly = q.isReadOnly === true || q.readOnly === true;

        if (promptKind && canAdd && isVisible && !isReadOnly) return promptKind;
      }

      // Fallback: inspect visible add buttons on the currently active rendered page.
      const activePageRoot =
        (document.querySelector(".sd-page.sd-page--active") as HTMLElement | null) ||
        (document.querySelector(".sd-page") as HTMLElement | null);
      if (!activePageRoot) return null;

      const addButtons = Array.from(
        activePageRoot.querySelectorAll(".sd-paneldynamic__add-btn")
      ) as HTMLElement[];

      for (const btn of addButtons) {
        if (btn.getAttribute("aria-hidden") === "true") continue;
        const text = (btn.textContent ?? "").toLocaleLowerCase();
        const promptKind = promptKindForText(text);
        if (promptKind) return promptKind;
      }
      return null;
    };

    model.onCurrentPageChanging.add(async (_, options: any) => {
      if (!options?.isNextPage) return;
      if (Date.now() - lastOrganizationPromptAcceptedAtRef.current < 400) return;
      const promptKind = getAddPromptKindOnCurrentPage(options);
      if (!promptKind) return;

      const proceed = await requestOrganizationConfirm(promptKind);

      if (!proceed) {
        options.allowChanging = false;
        options.allow = false;
        return;
      }

      lastOrganizationPromptAcceptedAtRef.current = Date.now();
    });

    model.onCompleting.add(async (_, options: any) => {
      if (Date.now() - lastOrganizationPromptAcceptedAtRef.current < 400) return;
      const promptKind = getAddPromptKindOnCurrentPage(undefined);
      if (!promptKind) return;

      const proceed = await requestOrganizationConfirm(promptKind);

      if (!proceed) {
        options.allow = false;
        options.allowComplete = false;
        return;
      }

      lastOrganizationPromptAcceptedAtRef.current = Date.now();
    });

    // Realtime-hydrate dropdowns that reference Firestore lists
    const questionUnsubs: Array<() => void> = [];
    const disposeAllQuestionUnsubs = () => {
      questionUnsubs.splice(0).forEach((u) => {
        try {
          u();
        } catch {}
      });
    };
    unsubscribersRef.current.push(disposeAllQuestionUnsubs);

    model.onAfterRenderQuestion.add((_, opts: any) => {
      const q: Question | undefined = opts?.question;
      if (!q || q.getType() !== "dropdown") return;

      const listId = (q as any).getPropertyValue("firestoreListId") as string | undefined;
      if (!listId) return;

      const anyQ = q as any;
      if (typeof anyQ.__unsub === "function") {
        try {
          anyQ.__unsub();
        } catch {}
        anyQ.__unsub = undefined;
      }

      const itemsRef = collection(db, "lists", listId, "items");
      const unsub = onSnapshot(
        itemsRef,
        (snap) => {
          const choices = snap.docs.map((d) => {
            const v = d.data() as any;
            return { value: v.value ?? d.id, text: v.name ?? v.value ?? d.id };
          });
          choices.sort((a, b) => a.text.localeCompare(b.text));
          (q as any).choices = choices;
        },
        () => {
          (q as any).choices = [];
        }
      );

      anyQ.__unsub = unsub;
      questionUnsubs.push(unsub);

      // Normalize "Other"
      (q as any).hasOther = true;
      (q as any).otherText = "Other (describe)";
      (q as any).otherPlaceholder = "Enter new item here";
      (q as any).storeOthersAsComment = true; // ensures "<key>-Comment"
    });

    // Helper: flatten SurveyJS data for "Other" adds
    type FlatEntry = { question: Question; value: any; comment?: string };
    function flattenSurveyData(surveyData: any, surveyModel: Model): FlatEntry[] {
      const entries: FlatEntry[] = [];
      const allQuestions = surveyModel.getAllQuestions();

      allQuestions.forEach((q: any) => {
        const qName = q.name;
        const qType = q.getType();

        if (qType === "dropdown") {
          const value = surveyData[qName];
          const comment = surveyData[`${qName}-Comment`];
          if (value !== undefined) entries.push({ question: q, value, comment });
        } else if (qType === "paneldynamic") {
          const panelDataArray = surveyData[qName];
          if (Array.isArray(panelDataArray)) {
            panelDataArray.forEach((panelData: any) => {
              q.templateElements.forEach((tplQ: Question) => {
                const tName = (tplQ as any).name;
                const value = panelData[tName];
                const comment = panelData[`${tName}-Comment`];
                if (value !== undefined) entries.push({ question: tplQ, value, comment });
              });
            });
          }
        }
      });

      return entries;
    }

    // On complete: add "other" items to lists (requires signed-in; anonymous is ok) + save response
    model.onComplete.add(async (sender) => {
      if (submittingRef.current) return;
      submittingRef.current = true;

      try {
        const flat = flattenSurveyData(sender.data, model);

        for (const { question, value, comment } of flat) {
          const isOtherValue = value === "other" && comment;
          if (!isOtherValue) continue;

          const listId = (question as any).getPropertyValue("firestoreListId") as string | undefined;
          if (!listId) continue;

          const freeText = String(comment ?? "").trim();
          if (!freeText) continue;

          const itemsRef = collection(db, "lists", listId, "items");
          const nameLower = freeText.toLocaleLowerCase();

          // skip duplicates
          const existsSnap = await getDocs(query(itemsRef, where("nameLower", "==", nameLower)));
          if (!existsSnap.empty) continue;

          // attempt to add (will succeed if we have (anon) auth per rules)
          try {
            await addDoc(itemsRef, {
              name: freeText,
              value: freeText,
              nameLower,
              createdAt: serverTimestamp(),
              createdBy: auth.currentUser?.uid ?? null,
            });
          } catch {
            // Non-fatal: viewer may not be signed in; ignore failure
          }
        }

        // Save response (rules allow create if survey is published; auth not required)
        await addDoc(collection(db, "surveys", id!, "responses"), {
          answers: sender.data,
          submittedAt: serverTimestamp(),
          uid: auth.currentUser?.uid ?? null,
        });
      } finally {
        submittingRef.current = false;
      }
    });

    setSurveyModel(model);
    // cleanup handled by outer disposer
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, allowed, survey, id]);

  // UI states
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!allowed || !survey) {
    return <div style={{ color: "red", padding: 20 }}>Survey not available.</div>;
  }

  if (!surveyModel) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  // Main render
  return (
    <Box sx={{ p: 2 }}>
      <Survey model={surveyModel} />
      <Dialog
        open={orgConfirmOpen}
        onClose={() => resolveOrganizationConfirm(false)}
        aria-labelledby="org-add-confirm-title"
      >
        <DialogTitle id="org-add-confirm-title">Before you continue</DialogTitle>
        <DialogContent>
          <Typography>
            {addPromptKind === "person"
              ? "Did you add all the people you work with?"
              : "Did you add all the organizations you work with?"}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            You can stay on this page to add more {addPromptKind === "person" ? "people" : "organizations"},
            or continue if you are finished.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => resolveOrganizationConfirm(false)} variant="outlined">
            Stay on this page
          </Button>
          <Button onClick={() => resolveOrganizationConfirm(true)} variant="contained">
            Continue
          </Button>
        </DialogActions>
      </Dialog>
      {/* Subtle access tag at the very bottom */}
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
        Your access: {role}
      </Typography>
    </Box>
  );
}
